import type { CrudFeatureField } from '@org/design-system/generators';
import type { AuthConfig, EntityConfig } from '../types/app-config';
import { getStoredAuthToken } from '../auth/token-storage';

export interface ReferenceDisplayValue {
  id: string;
  label: string;
  href: string;
}

export type ReferenceFieldValueMap = Record<string, Record<string, ReferenceDisplayValue>>;

export function resolveReferenceEntity(
  reference: string | undefined,
  entities: EntityConfig[] | undefined,
): EntityConfig | undefined {
  if (!reference || !entities?.length) return undefined;
  const normalizedReference = reference.toLowerCase();

  return entities.find(entity => (
    entity.name.toLowerCase() === normalizedReference
    || entity.slug.toLowerCase() === normalizedReference
    || entity.pluralName.toLowerCase() === normalizedReference
  ));
}

function readStringValue(
  record: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

/**
 * Prioritizes first-name style fields, then title-like fields, then common fallbacks.
 */
export function referenceDisplayName(record: Record<string, unknown>): string {
  const firstName = readStringValue(record, ['firstName', 'first_name', 'givenName', 'given_name']);
  const lastName = readStringValue(record, ['lastName', 'last_name', 'familyName', 'family_name']);

  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;

  const title = readStringValue(record, ['title']);
  if (title) return title;

  const fallback = readStringValue(record, ['name', 'label', 'email', 'code']);
  if (fallback) return fallback;

  const id = record.id;
  return id !== undefined && id !== null ? String(id) : 'Unknown';
}

export function buildAuthHeaders(auth?: AuthConfig): Headers {
  const headers = new Headers({
    Accept: 'application/json',
  });

  const token = getStoredAuthToken(auth);
  if (!token) return headers;

  const tokenHeader = auth?.tokenHeader || 'Authorization';
  if (tokenHeader.toLowerCase() === 'authorization') {
    headers.set(tokenHeader, token.startsWith('Bearer ') ? token : `Bearer ${token}`);
  } else {
    headers.set(tokenHeader, token);
  }

  return headers;
}

export function collectReferenceFieldIds(
  fields: CrudFeatureField[],
  rows: Record<string, unknown>[],
): Array<{ field: CrudFeatureField; id: string }> {
  const values: Array<{ field: CrudFeatureField; id: string }> = [];

  fields.forEach(field => {
    if (field.type !== 'reference') return;

    const seen = new Set<string>();
    rows.forEach(row => {
      const raw = row[field.name];
      if (raw === undefined || raw === null || raw === '') return;
      const id = String(raw);
      if (seen.has(id)) return;
      seen.add(id);
      values.push({ field, id });
    });
  });

  return values;
}
